import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Typography,
  Paper,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  alpha,
  Stack,
  Divider,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Category as CategoryIcon,
  NotesOutlined as DescriptionIcon,
  CategoryOutlined as MainCategoryIcon,
  StraightenOutlined as UnitIcon,
  BadgeOutlined as ClientIdIcon,
  FingerprintOutlined as InternalIdIcon,
  EventOutlined as DateIcon,
  DataObjectOutlined as ExtraDataIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import { useSelfSetupTour } from '../../hooks/useSelfSetupTour';
import {
  addOrganizationWasteType,
  getOrganizationWasteTypes,
  removeOrganizationWasteType,
  updateOrganizationWasteType,
  type OrganizationWasteType,
} from '../../services/organization.service';
import { getAllWasteCatalogMaterials, type WasteCatalogOption } from '../../services/waste-catalog.service';
import { FormTabHintCoachmark, useFormTabHint } from '../../components/FormTabHintCoachmark';
import { elevatedPanelSx } from '../../constants/shape';
import { PageHeaderSkeleton } from '../../components/LoadingSkeleton';
import MaterialCategoryTable from '../../components/recycling/MaterialCategoryTable';
import MaterialCategorySectionsSkeleton from '../../components/recycling/MaterialCategoryTableSkeleton';
import EntityDetailRow from '../../components/common/EntityDetailRow';
import { getMaterialCategorySurface } from '../../constants/material-category-theme';
import { useConfirm } from '../../hooks/useConfirm';
import { useMaterialTypeStore } from '../../stores/recycling.store';
import { useOrganizationStore } from '../../stores/organization.store';
import {
  MaterialTypeSchema,
  MaterialTypeRequest,
  UpdateMaterialTypeRequest,
  MaterialsMainCategoriesTypes,
  MaterialUnitOptions,
} from '../../types/recycling.type';

const UNCATEGORIZED_KEY = '__uncategorized__';

const HIDDEN_EXTRA_DATA_KEYS = new Set(['client_id', 'seeded_default']);

const PAGE_CONTENT_MAX_WIDTH = 1840;

const dialogFieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
} as const;

const dialogFormGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 2,
  '& > .full-width': { gridColumn: '1 / -1' },
} as const;

const categoryGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
  },
  gap: 3,
  justifyItems: 'stretch',
  alignItems: 'start',
} as const;

const CATEGORY_ORDER = Object.values(MaterialsMainCategoriesTypes);

function formatLabel(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function groupMaterialsByCategory(
  materials: MaterialTypeSchema[],
  categoryLabel: (key: string) => string
): Array<{ categoryKey: string; label: string; items: MaterialTypeSchema[] }> {
  const buckets = new Map<string, MaterialTypeSchema[]>();

  materials.forEach((material) => {
    const key = material.parent_category?.trim() || UNCATEGORIZED_KEY;
    const list = buckets.get(key) ?? [];
    list.push(material);
    buckets.set(key, list);
  });

  const groups: Array<{ categoryKey: string; label: string; items: MaterialTypeSchema[] }> = [];

  CATEGORY_ORDER.forEach((cat) => {
    const items = buckets.get(cat);
    if (!items?.length) return;
    groups.push({
      categoryKey: cat,
      label: categoryLabel(cat),
      items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
    });
    buckets.delete(cat);
  });

  buckets.forEach((items, categoryKey) => {
    if (!items.length) return;
    groups.push({
      categoryKey,
      label:
        categoryKey === UNCATEGORIZED_KEY
          ? categoryLabel(UNCATEGORIZED_KEY)
          : categoryLabel(categoryKey),
      items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
    });
  });

  return groups;
}

function formatDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'â€”';
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function softChipSx(color: string) {
  return {
    height: 26,
    fontWeight: 600,
    bgcolor: alpha(color, 0.1),
    color,
    border: `1px solid ${alpha(color, 0.22)}`,
  } as const;
}

function ViewSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        display: 'block',
        mb: 1,
        mt: 0.25,
      }}
    >
      {children}
    </Typography>
  );
}

function DetailValue({
  children,
  muted,
  mono,
  preWrap,
  title,
}: {
  children: React.ReactNode;
  muted?: boolean;
  mono?: boolean;
  preWrap?: boolean;
  title?: string;
}) {
  return (
    <Typography
      variant="body2"
      title={title}
      sx={{
        fontWeight: muted ? 400 : 500,
        color: muted ? 'text.secondary' : 'text.primary',
        textAlign: 'end',
        fontFamily: mono ? 'monospace' : undefined,
        fontSize: mono ? '0.875rem' : undefined,
        whiteSpace: preWrap ? 'pre-wrap' : 'nowrap',
        overflow: preWrap ? undefined : 'hidden',
        textOverflow: preWrap ? undefined : 'ellipsis',
        wordBreak: mono ? 'break-all' : undefined,
        maxWidth: '100%',
      }}
    >
      {children}
    </Typography>
  );
}

function hasText(value: unknown): boolean {
  return value != null && String(value).trim() !== '';
}

const MaterialTypes: React.FC = () => {
  const { t } = useTranslation();
  const { confirm, confirmDialog } = useConfirm();
  // Organizations pick waste types from the shared catalog; they no longer define
  // their own. The basket is the selection, and the client code is theirs alone.
  const [catalogOptions, setCatalogOptions] = useState<WasteCatalogOption[]>([]);
  const [basketRows, setBasketRows] = useState<OrganizationWasteType[]>([]);
  const [pickedMaterial, setPickedMaterial] = useState<WasteCatalogOption | null>(null);
  const [clientCustomId, setClientCustomId] = useState('');
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dismissAddHint, setDismissAddHint] = useState(false);
  /** Knowledge Base "Go to Add material" guided tips. */
  type HelpAddPhase = 'addButton' | 'form' | 'submit';
  const [helpAddPhase, setHelpAddPhase] = useState<HelpAddPhase | null>(null);
  /** Knowledge Base "Go to Edit or delete material" row-action tips. */
  type HelpRowActionsPhase = 'edit' | 'delete';
  const [helpRowActionsPhase, setHelpRowActionsPhase] =
    useState<HelpRowActionsPhase | null>(null);
  const { isStep, tourPhase, completeStep, dismissTour, setTourPhase, advanceTourPhase } =
    useSelfSetupTour();
  const tourTableHint = isStep('materials') && tourPhase === 'table';
  const tourAddHint = isStep('materials') && tourPhase === 'addButton';
  const tourFormHint =
    (isStep('materials') && tourPhase === 'form') || helpAddPhase === 'form';
  const tourSubmitHint =
    (isStep('materials') && tourPhase === 'submit') || helpAddPhase === 'submit';
  const tourDialogOpen = tourFormHint || tourSubmitHint;
  const [guideHints, setGuideHints] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewMaterial, setViewMaterial] = useState<MaterialTypeSchema | null>(null);
  const [selectedMaterial, setSelectedMaterial] =
    useState<MaterialTypeSchema | null>(null);

  const [formData, setFormData] = useState<MaterialTypeRequest>({
    name: '',
    description: '',
    parent_category: '',
    organization_id: '',
    unit_of_measure: '',
    extra_data: {},
  });

  const {
    materialTypes,
    materialTypesLoading,
    fetchMaterialTypes,
    createMaterialType,
    updateMaterialType,
    deleteMaterialType,
  } = useMaterialTypeStore();
  const { selectedOrganization } = useOrganizationStore();

  const categoryLabel = useCallback(
    (key: string) =>
      key === UNCATEGORIZED_KEY
        ? t('materialTypes.uncategorized')
        : t(`materialTypes.categories.${key}`, { defaultValue: formatLabel(key) }),
    [t]
  );

  const groupedMaterials = useMemo(
    () => groupMaterialsByCategory(materialTypes, categoryLabel),
    [materialTypes, categoryLabel]
  );

  useEffect(() => {
    if (selectedOrganization) {
      fetchMaterialTypes(selectedOrganization.id);
    }
  }, [fetchMaterialTypes, selectedOrganization]);


  const organizationId = selectedOrganization?.id;

  const loadBasket = useCallback(async () => {
    if (!organizationId) return;
    try {
      const response = await getOrganizationWasteTypes(organizationId);
      const rows = (response?.result ?? response) as OrganizationWasteType[];
      setBasketRows(Array.isArray(rows) ? rows : []);
    } catch {
      setBasketRows([]);
    }
  }, [organizationId]);

  useEffect(() => {
    void loadBasket();
  }, [loadBasket]);

  useEffect(() => {
    // The whole catalog: this is what the organization chooses from.
    void getAllWasteCatalogMaterials()
      .then(setCatalogOptions)
      .catch(() => setCatalogOptions([]));
  }, []);

  /** The organization's own code for a waste type, by material. */
  const clientIdByMaterial = useMemo(
    () => new Map(basketRows.map((row) => [row.material_id, row.client_custom_id ?? ''])),
    [basketRows],
  );

  /** Only what is not already selected -- adding a duplicate is a conflict. */
  const addableMaterials = useMemo(() => {
    const held = new Set(basketRows.map((row) => row.material_id));
    return catalogOptions.filter((option) => !held.has(option.value));
  }, [catalogOptions, basketRows]);

  const handleOpen = (material?: MaterialTypeSchema) => {
    if (material) {
      setSelectedMaterial(material);
      setPickedMaterial(null);
      setClientCustomId(clientIdByMaterial.get(material.material_id ?? '') ?? '');
      setFormData({
        name: material.name || '',
        description: material.description || '',
        parent_category: material.parent_category ?? '',
        organization_id: material.organization_id ?? '',
        unit_of_measure: material.unit_of_measure ?? '',
        extra_data: material.extra_data ?? {},
      });
    } else {
      setSelectedMaterial(null);
      setPickedMaterial(null);
      setClientCustomId('');
      setFormData({
        name: '',
        description: '',
        parent_category: '',
        organization_id: '',
        unit_of_measure: '',
        extra_data: {},
      });
    }
    setOpen(true);
  };

  useEffect(() => {
    const state = location.state as {
      openAdd?: boolean;
      showAddHint?: boolean;
      showRowActionsHint?: boolean;
    } | null;
    if (!state?.openAdd && !state?.showAddHint && !state?.showRowActionsHint) return;
    if (state.openAdd) handleOpen();
    if (state.showAddHint) {
      setHelpAddPhase('addButton');
      setDismissAddHint(false);
    }
    if (state.showRowActionsHint) {
      setHelpRowActionsPhase('edit');
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const finishHelpAddGuide = () => {
    setHelpAddPhase(null);
    setOpen(false);
    setSelectedMaterial(null);
    setGuideHints(false);
  };

  const handleClose = () => {
    if (helpAddPhase) {
      finishHelpAddGuide();
      return;
    }
    // Park the tour before closing so dialog tips do not fight the close.
    if (isStep('materials') && (tourPhase === 'form' || tourPhase === 'submit')) {
      dismissTour();
    }
    setOpen(false);
    setSelectedMaterial(null);
    setGuideHints(false);
  };

  const handleView = (material: MaterialTypeSchema) => {
    setViewMaterial(material);
    setViewDialogOpen(true);
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setViewMaterial(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationId) return;
    // Editing changes only the organization's own code; adding takes a waste type
    // from the catalog into its basket. Neither creates a waste type -- that is the
    // catalog's job, and Admin's.
    if (selectedMaterial) {
      await updateOrganizationWasteType(organizationId, selectedMaterial.material_id ?? '', clientCustomId);
    } else {
      if (!pickedMaterial) return;
      await addOrganizationWasteType(organizationId, pickedMaterial.value, clientCustomId);
    }
    await loadBasket();
    await fetchMaterialTypes(organizationId);
    handleClose();
  };

  const handleDelete = async (material: MaterialTypeSchema) => {
    if (await confirm({ title: t('common.delete'), description: t('materialTypes.confirmDelete'), tone: 'danger' })) {
      if (viewMaterial?.id === material.id) {
        handleCloseView();
      }
      // Only the selection goes; records already using it are untouched.
      if (organizationId && material.material_id) {
        await removeOrganizationWasteType(organizationId, material.material_id);
        await loadBasket();
        await fetchMaterialTypes(organizationId);
      }
    }
  };

  const extraDataEntries = useMemo(() => {
    if (!viewMaterial?.extra_data) return [];
    return Object.entries(viewMaterial.extra_data).filter(
      ([key, value]) =>
        !HIDDEN_EXTRA_DATA_KEYS.has(key) && value != null && value !== ''
    );
  }, [viewMaterial]);

  const viewCategoryKey =
    viewMaterial?.parent_category?.trim() || UNCATEGORIZED_KEY;
  const viewSurface = viewMaterial
    ? getMaterialCategorySurface(viewCategoryKey, theme)
    : null;
  const ViewCategoryIcon = viewSurface?.Icon;

  const formCategoryKey = formData.parent_category?.trim() || '';
  const formSurface = formCategoryKey
    ? getMaterialCategorySurface(formCategoryKey, theme)
    : null;
  const FormCategoryIcon = formSurface?.Icon;

  // Adding needs a waste type chosen from the catalog; editing needs nothing, since
  // the client code is optional and everything else belongs to the catalog.
  const requiredFieldsComplete = Boolean(selectedMaterial || pickedMaterial);

  const [fieldsHintDismissed, setFieldsHintDismissed] = useState(false);
  const [submitHintDismissed, setSubmitHintDismissed] = useState(false);
  const [submitHintAnchor, setSubmitHintAnchor] = useState<HTMLElement | null>(null);
  const submitHintRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      setSubmitHintAnchor(null);
      return;
    }
    setFieldsHintDismissed(false);
    setSubmitHintDismissed(false);
  }, [open, selectedMaterial]);

  const {
    showHint: showFieldsHint,
    dismissHint: dismissFieldsHint,
    hintAnchor: fieldsHintAnchor,
    hintPlacement: fieldsHintPlacement,
    setTabRef,
    getTabSx,
  } = useFormTabHint({
    enabled:
      !selectedMaterial &&
      !fieldsHintDismissed &&
      guideHints &&
      !tourFormHint &&
      !tourSubmitHint,
    dialogOpen: open,
    activeTab: 0,
    side: 'left',
  });

  const showSubmitHint =
    open && !selectedMaterial && guideHints && requiredFieldsComplete && !submitHintDismissed;

  // As soon as * fields are done, focus the Add tip (hide field tips).
  useEffect(() => {
    if (!showSubmitHint || fieldsHintDismissed) return;
    setFieldsHintDismissed(true);
  }, [showSubmitHint, fieldsHintDismissed]);

  useEffect(() => {
    if (!requiredFieldsComplete) {
      setSubmitHintDismissed(false);
    }
  }, [requiredFieldsComplete]);

  useLayoutEffect(() => {
    if (!showSubmitHint) return;
    const el = submitHintRef.current;
    if (el) setSubmitHintAnchor(el);
  }, [showSubmitHint, requiredFieldsComplete]);

  const handleDismissFieldsHint = () => {
    setFieldsHintDismissed(true);
    dismissFieldsHint();
  };

  const handleDismissSubmitHint = () => {
    setSubmitHintDismissed(true);
  };

  const submitHintPlacement = theme.direction === 'rtl' ? 'left' : 'right';
  const submitHintSx = showSubmitHint
    ? {
        position: 'relative' as const,
        zIndex: 1,
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 3,
        borderRadius: 1.5,
        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
      }
    : undefined;

  // Self-setup: open create dialog once when entering form/submit tip phases.
  useEffect(() => {
    if (!tourDialogOpen) return;
    if (open || selectedMaterial) return;
    handleOpen();
    // Only react to entering dialog tip phases — not to dialog close.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/selectedMaterial intentionally omitted
  }, [tourDialogOpen]);

  const showAddHint =
    !dismissAddHint &&
    !materialTypesLoading &&
    !open &&
    (tourAddHint ||
      (helpAddPhase === 'addButton' &&
        !(
          isStep('materials') &&
          (tourPhase === 'nav' ||
            tourPhase === 'table' ||
            tourPhase === 'form' ||
            tourPhase === 'submit')
        )));

  if (materialTypesLoading) {
    return (
      <Box>
        <Box sx={{ maxWidth: PAGE_CONTENT_MAX_WIDTH, mx: 'auto', width: '100%' }}>
          <PageHeaderSkeleton />
          <MaterialCategorySectionsSkeleton sections={2} sx={categoryGridSx} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ maxWidth: PAGE_CONTENT_MAX_WIDTH, mx: 'auto', width: '100%' }}>
      <PageHeader
        icon={<CategoryIcon />}
        title={t('materialTypes.title')}
        actions={
          <SetupCoachmark
            open={showAddHint}
            title={t('materialTypes.addMaterialHintTitle')}
            subtitle={t('materialTypes.addMaterialHint')}
            nextLabel={t('selfSetup.tourNext')}
            onNext={() => {
              setDismissAddHint(true);
              if (tourAddHint) {
                advanceTourPhase();
                return;
              }
              if (helpAddPhase === 'addButton') {
                setHelpAddPhase('form');
                handleOpen();
              }
            }}
            onDismiss={() => {
              setDismissAddHint(true);
              if (tourAddHint) dismissTour();
              else if (helpAddPhase === 'addButton') setHelpAddPhase(null);
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setDismissAddHint(true);
                handleOpen();
                // Materials tour continues into the form tip (not complete yet).
                if (tourAddHint) setTourPhase('form');
                else if (helpAddPhase === 'addButton') setHelpAddPhase('form');
              }}
              sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}
            >
              {t('materialTypes.addMaterial')}
            </Button>
          </SetupCoachmark>
        }
      />

      {groupedMaterials.length === 0 ? (
        <SetupCoachmark
          open={tourTableHint || Boolean(helpRowActionsPhase)}
          fullWidth
          title={
            helpRowActionsPhase
              ? t('materialTypes.editDeleteMaterialEmptyTitle')
              : t('selfSetup.tour.materialsTableTitle')
          }
          subtitle={
            helpRowActionsPhase
              ? t('materialTypes.editDeleteMaterialEmptyHint')
              : t('selfSetup.tour.materialsTableSubtitle')
          }
          nextLabel={
            helpRowActionsPhase ? t('selfSetup.tourDone') : t('selfSetup.tourNext')
          }
          onNext={() => {
            if (helpRowActionsPhase) {
              setHelpRowActionsPhase(null);
              return;
            }
            advanceTourPhase();
          }}
          onDismiss={() => {
            if (helpRowActionsPhase) {
              setHelpRowActionsPhase(null);
              return;
            }
            dismissTour();
          }}
        >
          <Paper
            elevation={2}
            sx={{
              ...elevatedPanelSx,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('materialTypes.emptyTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('materialTypes.emptyDescription')}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={tourTableHint || tourAddHint || Boolean(helpRowActionsPhase)}
              onClick={() => handleOpen()}
            >
              {t('materialTypes.addMaterial')}
            </Button>
          </Paper>
        </SetupCoachmark>
      ) : (
        <SetupCoachmark
          open={tourTableHint}
          fullWidth
          title={t('selfSetup.tour.materialsTableTitle')}
          subtitle={t('selfSetup.tour.materialsTableSubtitle')}
          nextLabel={t('selfSetup.tourNext')}
          onNext={() => advanceTourPhase()}
          onDismiss={() => dismissTour()}
        >
          <Box sx={categoryGridSx}>
            {groupedMaterials.map((group, groupIndex) => (
              <MaterialCategoryTable
                key={group.categoryKey}
                group={group}
                formatLabel={formatLabel}
                onView={handleView}
                onEdit={handleOpen}
                onDelete={handleDelete}
                helpTarget={groupIndex === 0}
                helpRowActionsPhase={helpRowActionsPhase}
                onHelpRowActionsNext={() => setHelpRowActionsPhase('delete')}
                onHelpRowActionsDismiss={() => setHelpRowActionsPhase(null)}
              />
            ))}
          </Box>
        </SetupCoachmark>
      )}

      </Box>

      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseView}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            bgcolor: viewSurface
              ? alpha(viewSurface.color, 0.06)
              : alpha(theme.palette.primary.main, 0.06),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {viewMaterial ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: viewSurface?.iconBg ?? alpha(theme.palette.primary.main, 0.12),
                  color: viewSurface?.color ?? theme.palette.primary.main,
                  flexShrink: 0,
                }}
              >
                {ViewCategoryIcon ? (
                  <ViewCategoryIcon sx={{ fontSize: 22 }} />
                ) : (
                  <CategoryIcon sx={{ fontSize: 22 }} />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  {viewMaterial.parent_category
                    ? categoryLabel(viewMaterial.parent_category)
                    : t('materialTypes.uncategorized')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}
                  noWrap
                  title={viewMaterial.name}
                >
                  {viewMaterial.name}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 700, m: 0 }}>
              {t('materialTypes.materialDetails')}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 2 }}>
          {viewMaterial && (
            <Stack spacing={2} divider={<Divider flexItem sx={{ opacity: 0.55 }} />}>
              <Box>
                <ViewSectionTitle>{t('materialTypes.viewSections.overview')}</ViewSectionTitle>
                <Stack spacing={1.25}>
                  <EntityDetailRow icon={<MainCategoryIcon />} label={t('materialTypes.mainCategory')}>
                    {viewMaterial.parent_category ? (
                      <Chip
                        size="small"
                        label={categoryLabel(viewMaterial.parent_category)}
                        sx={softChipSx(viewSurface?.color ?? theme.palette.primary.main)}
                      />
                    ) : (
                      <DetailValue muted>â€”</DetailValue>
                    )}
                  </EntityDetailRow>

                  <EntityDetailRow icon={<UnitIcon />} label={t('materialTypes.unitOfMeasure')}>
                    {viewMaterial.unit_of_measure ? (
                      <Chip
                        size="small"
                        label={t(`units.${viewMaterial.unit_of_measure}`, {
                          defaultValue: formatLabel(viewMaterial.unit_of_measure),
                        })}
                        sx={softChipSx(theme.palette.info.main)}
                      />
                    ) : (
                      <DetailValue muted>â€”</DetailValue>
                    )}
                  </EntityDetailRow>

                  {hasText(viewMaterial.description) && (
                    <EntityDetailRow icon={<DescriptionIcon />} label={t('common.description')}>
                      <DetailValue preWrap>{viewMaterial.description?.trim()}</DetailValue>
                    </EntityDetailRow>
                  )}
                </Stack>
              </Box>

              {/* The client code is this organization's own, so it comes from its basket
                  row rather than the shared catalog entry. */}
              {hasText(clientIdByMaterial.get(viewMaterial.material_id ?? '')) && (
                <Box>
                  <ViewSectionTitle>{t('materialTypes.viewSections.identifiers')}</ViewSectionTitle>
                  <Stack spacing={1.25}>
                    <EntityDetailRow icon={<ClientIdIcon />} label={t('materialTypes.clientCustomId')}>
                      <DetailValue>{clientIdByMaterial.get(viewMaterial.material_id ?? '')}</DetailValue>
                    </EntityDetailRow>
                  </Stack>
                </Box>
              )}

              {extraDataEntries.length > 0 && (
                <Box>
                  <ViewSectionTitle>{t('materialTypes.additionalData')}</ViewSectionTitle>
                  <Stack spacing={1.25}>
                    {extraDataEntries.map(([key, value]) => (
                      <EntityDetailRow
                        key={key}
                        icon={<ExtraDataIcon />}
                        label={formatLabel(key)}
                      >
                        <DetailValue preWrap>
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </DetailValue>
                      </EntityDetailRow>
                    ))}
                  </Stack>
                </Box>
              )}

              <Box>
                <ViewSectionTitle>{t('materialTypes.viewSections.timeline')}</ViewSectionTitle>
                <Stack spacing={1.25}>
                  <EntityDetailRow icon={<DateIcon />} label={t('materialTypes.createdAt')}>
                    <DetailValue muted>{formatDateTime(viewMaterial.created_at)}</DetailValue>
                  </EntityDetailRow>

                  <EntityDetailRow icon={<DateIcon />} label={t('materialTypes.updatedAt')}>
                    <DetailValue muted>{formatDateTime(viewMaterial.updated_at)}</DetailValue>
                  </EntityDetailRow>

                  <EntityDetailRow icon={<InternalIdIcon />} label={t('common.id')}>
                    <DetailValue mono title={viewMaterial.id}>
                      {viewMaterial.id}
                    </DetailValue>
                  </EntityDetailRow>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={handleCloseView} sx={{ textTransform: 'none' }}>
            {t('common.close')}
          </Button>
          {viewMaterial && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                const material = viewMaterial;
                handleCloseView();
                handleOpen(material);
              }}
              sx={{ textTransform: 'none' }}
            >
              {t('common.edit')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            bgcolor: formSurface
              ? alpha(formSurface.color, 0.06)
              : alpha(theme.palette.primary.main, 0.06),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: formSurface
                  ? formSurface.iconBg
                  : alpha(theme.palette.primary.main, 0.12),
                color: formSurface ? formSurface.color : theme.palette.primary.main,
                flexShrink: 0,
              }}
            >
              {FormCategoryIcon ? (
                <FormCategoryIcon sx={{ fontSize: 22 }} />
              ) : selectedMaterial ? (
                <EditIcon sx={{ fontSize: 22 }} />
              ) : (
                <AddIcon sx={{ fontSize: 22 }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {selectedMaterial ? t('common.edit') : t('common.add')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
                {selectedMaterial
                  ? t('materialTypes.editDialogTitle')
                  : t('materialTypes.addDialogTitle')}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ px: 2.5, py: 2.5 }}>
            <SetupCoachmark
              open={tourFormHint && open && !selectedMaterial}
              fullWidth
              title={t('selfSetup.tour.materialsFormTitle')}
              subtitle={t('selfSetup.tour.materialsFormSubtitle')}
              nextLabel={t('selfSetup.tourNext')}
              onNext={() => {
                if (helpAddPhase === 'form') {
                  setHelpAddPhase('submit');
                  return;
                }
                advanceTourPhase();
              }}
              onDismiss={() => {
                if (helpAddPhase) {
                  finishHelpAddGuide();
                  return;
                }
                dismissTour();
                setOpen(false);
                setSelectedMaterial(null);
                setGuideHints(false);
              }}
            >
              <Box
                ref={setTabRef(0)}
                sx={{
                  ...dialogFormGridSx,
                  p: showFieldsHint ? 1.5 : 0,
                  m: showFieldsHint ? -1.5 : 0,
                  borderRadius: 2,
                  transition: 'box-shadow 0.2s ease, outline 0.2s ease',
                  ...(getTabSx(0) || {}),
                }}
              >
                {selectedMaterial ? (
                  <TextField
                    className="full-width"
                    label={t('materialTypes.materialName')}
                    value={formData.name}
                    disabled
                    fullWidth
                    size="small"
                    sx={dialogFieldSx}
                    helperText={t(
                      'materialTypes.catalogOwned',
                      'Defined in the central catalog. Only your own reference code is editable here.',
                    )}
                  />
                ) : (
                  <Autocomplete
                    className="full-width"
                    options={addableMaterials}
                    value={pickedMaterial}
                    onChange={(_event, value) => setPickedMaterial(value)}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    groupBy={(option) => option.category ?? ''}
                    noOptionsText={t(
                      'materialTypes.allSelected',
                      'Every catalog waste type is already selected.',
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('materialTypes.materialName')}
                        required
                        fullWidth
                        size="small"
                        sx={dialogFieldSx}
                        helperText={t(
                          'materialTypes.pickFromCatalog',
                          'Chosen from the central catalog. Ask an administrator if something is missing.',
                        )}
                      />
                    )}
                  />
                )}

                <TextField
                  className="full-width"
                  label={t('materialTypes.clientCustomId')}
                  value={clientCustomId}
                  onChange={(e) => setClientCustomId(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 64 }}
                  sx={dialogFieldSx}
                  helperText={t(
                    'materialTypes.clientCustomIdHelp',
                    'Optional. Your own reference code — only your organization sees it.',
                  )}
                />
              </Box>
            </SetupCoachmark>

            <FormTabHintCoachmark
              open={showFieldsHint}
              anchorEl={fieldsHintAnchor}
              placement={fieldsHintPlacement}
              title={t('materialTypes.formHints.title', {
                defaultValue: 'Material details',
              })}
              subtitle={t('materialTypes.formHints.subtitle', {
                defaultValue: 'Enter name, category, and unit.',
              })}
              onDismiss={handleDismissFieldsHint}
            />

            <FormTabHintCoachmark
              open={showSubmitHint}
              anchorEl={submitHintAnchor}
              placement={submitHintPlacement}
              allowFlip
              title={t('materialTypes.formHints.submitTitle', {
                defaultValue: 'Ready to save',
              })}
              subtitle={t('materialTypes.formHints.submitSubtitle', {
                defaultValue: 'Required fields done — click Add.',
              })}
              onDismiss={handleDismissSubmitHint}
            />
          </DialogContent>

          <DialogActions
            sx={{
              px: 2.5,
              py: 2,
              gap: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
              {t('common.cancel')}
            </Button>

            <Box
              ref={submitHintRef}
              sx={{
                display: 'inline-flex',
                borderRadius: 1.5,
                ...(submitHintSx || {}),
              }}
            >
              <SetupCoachmark
                open={tourSubmitHint && open && !selectedMaterial}
                title={t('selfSetup.tour.materialsSubmitTitle')}
                subtitle={t('selfSetup.tour.materialsSubmitSubtitle')}
                nextLabel={t('selfSetup.tourDone')}
                onNext={() => {
                  if (helpAddPhase === 'submit') {
                    finishHelpAddGuide();
                    return;
                  }
                  completeStep('materials');
                  setOpen(false);
                  setSelectedMaterial(null);
                  setGuideHints(false);
                }}
                onDismiss={() => {
                  if (helpAddPhase) {
                    finishHelpAddGuide();
                    return;
                  }
                  dismissTour();
                  setOpen(false);
                  setSelectedMaterial(null);
                  setGuideHints(false);
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: 'none', px: 2.5 }}
                >
                  {selectedMaterial ? t('common.update') : t('common.add')}
                </Button>
              </SetupCoachmark>
            </Box>
          </DialogActions>
        </form>
      </Dialog>
      {confirmDialog}
    </Box>
  );
};

export default MaterialTypes;

